<template>
    <UPageSection
        :reverse="largerThanLg"
        :title="title"
        :ui="{ title: '!text-primary !text-4xl' }"
        orientation="horizontal"
    >
        <div class="relative isolate size-full min-h-112 sm:min-h-145 lg:min-h-160">
            <MotionNuxtImg
                v-if="showMascot"
                :in-view-options="motionInViewOptions"
                :initial="motionInitial"
                :transition="{...motionTransitionBase, delay: mascotDelay}"
                :while-in-view="motionWhileInView"
                alt="Maskottchen"
                class="z-50 h-1/2 absolute bottom-0 left-0 drop-shadow-lg"
                format="webp"
                src="Mukki_Front2.png"
            />
            <div class="-z-10 flex justify-center gap-4 w-full">
                <MotionBlogHexagonImage
                    v-for="(item, index) in props.hexagonImages"
                    :key="index"
                    :in-view-options="{once: true}"
                    :initial="motionInitial"
                    :transition="{...motionTransitionBase, delay: index * hexagonDelayStep}"
                    :while-in-view="motionWhileInView"
                    :class="index === 2 ? 'absolute left-1/2 top-0 -translate-x-1/2 translate-y-[79%]' : ''"
                    :img-src="useStrapiMedia(item.image?.url ?? '')"
                    class="max-w-[calc(50%-8px)] max-h-62.5 sm:max-h-80.5 lg:max-h-89.5 sm:max-w-1/3 lg:max-w-1/2"
                />
            </div>
        </div>
        <template #title>
            <motion.div
                :in-view-options="motionInViewOptions"
                :initial="motionInitial"
                :transition="{...motionTransitionBase, delay: titleDelay}"
                :while-in-view="motionWhileInView"
            >
                {{ title }}
            </motion.div>
        </template>
        <template #description>
            <motion.div
                :in-view-options="motionInViewOptions"
                :initial="motionInitial"
                :transition="{...motionTransitionBase, delay: descriptionDelay}"
                :while-in-view="motionWhileInView"
            >
                <StrapiBlocksText
                    v-if="teaserBlockNodes"
                    :nodes="teaserBlockNodes"
                />
            </motion.div>
        </template>
        <template #body>
            <motion.div
                :in-view-options="motionInViewOptions"
                :initial="motionInitial"
                :transition="{...motionTransitionBase, delay: bodyDelay}"
                :while-in-view="motionWhileInView"
            >
                <StrapiBlocksText
                    v-if="contentBlockNodes"
                    :nodes="contentBlockNodes"
                />
            </motion.div>
        </template>
    </UPageSection>
</template>

<script lang="ts" setup>
import type {PropType} from 'vue';
import {useBreakpoints, breakpointsTailwind} from '@vueuse/core';
import {motion} from 'motion-v';
import type {Media} from '~~/types/strapi-blog';
import type {BlockNode} from '#strapi-blocks-renderer/types';

type HexagonItem = { image?: Media | null }

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    teaser: {
        type: Array as PropType<BlockNode[]>,
        default: () => [],
    },
    introContent: {
        type: Array as PropType<BlockNode[]>,
        default: () => [],
    },
    hexagonImages: {
        type: Array as PropType<HexagonItem[]>,
        default: () => [],
    },
    showMascot: {
        type: Boolean,
        default: true,
    },
});

const breakpoints = useBreakpoints(breakpointsTailwind, {
    ssrWidth: 1280,
});

const largerThanLg = breakpoints.greaterOrEqual('lg');

const contentBlockNodes = computed(() => props.introContent);

const teaserBlockNodes = computed(() => props.teaser);

const BlogHexagonImage = resolveComponent('BlogHexagonImage');
const NuxtImg = resolveComponent('NuxtImg');

const MotionBlogHexagonImage = motion.create(BlogHexagonImage);
const MotionNuxtImg = motion.create(NuxtImg);

const motionInitial = {
    opacity: 0,
    y: 20,
};
const motionWhileInView = {
    opacity: 1,
    y: 0,
};
const motionInViewOptions = {once: true};
const motionTransitionBase = {
    duration: 0.8,
    ease: 'easeOut',
};
const titleDelay = 0.25;
const descriptionDelay = 0.35;
const bodyDelay = 0.45;
const mascotDelay = 0.3;
const hexagonDelayStep = 0.15;
</script>
